import React, { useEffect, useState } from 'react';
import { Pressable, Text, Switch, View, Picker, TextInput } from 'react-native';
import { useMutation } from '@apollo/client';
import styled from 'styled-components/native';
import webStyled from 'styled-components';

import Modal from 'app/src/elements/Modal';
import * as colors from 'app/src/styles/colors';

import NoSaleModal from './NoSaleModal';
import { UPDATE_SALE_ASSET } from './queries';
import { currency } from './currency';

const Container = styled(View)`
  flex-direction: row;
  gap: 10px;
`;

const StyledTextInput = webStyled.input`
  border-width: 1px;
  border-color: black;
  padding: 4px;
  width: 100px;
`;

export const StyledPicker = styled(Picker)`
  border-color: black;
  padding: 4px;
  font-size: 14px;
`;

const SaleAssetField = props => {
  const {
    saleAsset, field, isCurrency, isDate, isInt, isToggle, asset, offer,
    paddleServices, paddleServiceAddedField, selectOptions,
    activeTrackColor
  } = props;

  let { style } = props;

  let savedValue = saleAsset[field];

  if ('price' === field) {
    savedValue = saleAsset.activeOffer.soldPrice ||
      saleAsset.activeOffer.price;
  } else if (asset) {
    savedValue = saleAsset.asset[field];
  } else if (offer) {
    savedValue = saleAsset.activeOffer[field];
  } else if (paddleServiceAddedField) {
    savedValue = saleAsset.paddleServicesAdded?.includes(field);
  }

  if (savedValue && isCurrency) { savedValue = currency(savedValue); }

  useEffect(() => {
    if (savedValue !== value) {
      setValue(savedValue);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [savedValue]);

  const [value, setValue] = useState(savedValue);
  const [updateSaleAsset] = useMutation(UPDATE_SALE_ASSET);
  const [showNoSaleModal, setShowNoSaleModal] = useState(false);

  const updateFieldWithValue = newValue => {
    if (!newValue && !isToggle) { newValue = null; }

    if ('price' === field && 0.0 === parseFloat(newValue)) {
      setShowNoSaleModal(true);
      return;
    }

    if (isCurrency) {
      setValue(currency(value));
    }

    const variables = { id: saleAsset.id };
    if (asset) {
      variables.assetAttributes = { [field]: newValue };
    } else if (offer) {
      variables.offerAttributes = { [field]: newValue };
    } else if (paddleServiceAddedField) {
      variables.paddleServiceCode = field;
      variables.paddleServiceAdded = newValue;
    } else if (isInt) {
      if (newValue) {
        variables[field] = parseInt(newValue, 10);
      } else {
        variables[field] = null;
      }
    } else {
      variables[field] = newValue;
    }

    if (savedValue !== newValue) {
      updateSaleAsset({ variables });
    }
  };

  const updateField = () => {
    updateFieldWithValue(value);
  };

  const updateValueAndField = newValue => {
    setValue(newValue);
    updateFieldWithValue(newValue);
  };

  const handleKeyPress = e => {
    if ('Enter' === e.nativeEvent.key) {
      updateField();
    }
  };

  if (!style) { style = {}; }
  if (isDate) { style.width = 110; }

  let input = (
    <StyledTextInput
      onChange={e => setValue(e.target.value)}
      value={value || ''}
      onBlur={updateField}
      onKeyPress={handleKeyPress}
      style={style}
      type={isDate ? 'date' : 'text'}
    />
  );

  if (isToggle) {
    input = (
      <Switch
        value={value}
        onValueChange={updateValueAndField}
        activeTrackColor={activeTrackColor || colors.elGreen}
        activeThumbColor={colors.elGray}
        style={{marginHorizontal: 10}}
      />
    );
  } else if (selectOptions) {
    let selectValue = value;

    if ('lotNumberYear' === field) {
      selectValue = value ? String(value).padStart(2, '0') : ''
    }

    input = (
      <StyledPicker
        selectedValue={selectValue || ''}
        onValueChange={updateValueAndField}
      >
        <Picker.Item
          key={`${field}-default`}
          value=""
          label=""
        />
        {selectOptions.map(option => (
          <Picker.Item
            key={option}
            value={option}
            label={option}
          />
        ))}
      </StyledPicker>
    );
  } else if ('paddleServiceId' === field) {
    input = (
      <StyledPicker
        selectedValue={value || ''}
        onValueChange={updateValueAndField}
      >
        <Picker.Item
          key="service-default"
          value=""
          label="--"
        />
        {paddleServices?.map(service => (
          <Picker.Item
            key={service.id}
            value={service.id}
            label={service.code}
          />
        ))}
      </StyledPicker>
    );
  }

  return (
    <Container>
      {input}
      {showNoSaleModal && (
        <Modal
          showModal={showNoSaleModal}
          setShowModal={setShowNoSaleModal}
          modalType="No Sale"
          objectName={saleAsset.asset.stockNumber}
          saleAsset={saleAsset}
        >
          <NoSaleModal
            saleAsset={saleAsset}
            updateSaleAsset={updateSaleAsset}
            setShowModal={setShowNoSaleModal}
            clearField={() => updateValueAndField(0)}
          />
        </Modal>
      )}
    </Container>
  );
};

export default SaleAssetField;
